// Steg 1 for oppgaver som gjelder én kunde («Registrer trykk», «Ny ordre» …): finn eller
// registrer kunden, så åpnes riktig skjema med kunden valgt.
//
// Forsiden sender { sok, forslag } i navigasjons-state når et søk ikke ga treff, så skjemaet
// for ny kunde åpnes med det som ble skrevet (se forslagFraSok.js).
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { isLeder } from '../employeeAccess';
import { apneButikkmodus } from '../utils/butikkmodus';
import CustomerForm from '../CustomerForm';
import { finnHandling, FARGER, FORSIDE } from './handlinger';
import Ikon from './Ikon';
import KundeSok from './KundeSok';
import { forslagFraSok, forslagTekst } from './forslagFraSok';
import AktiveSkiplukk from '../skiplukk/AktiveSkiplukk';

const TOMT_FORSLAG = { firstName: '', lastName: '', phoneNumber: '', email: '' };

export default function VelgKunde({ customers, addCustomer }) {
  const { handlingId } = useParams();
  const navigate = useNavigate();
  const { state: fraForsiden } = useLocation();
  const handling = finnHandling(handlingId);
  const [sok, setSok] = useState(fraForsiden?.sok || '');
  const [visSkjema, setVisSkjema] = useState(Boolean(handling?.nyKunde || fraForsiden?.forslag));
  // Det skjemaet starter med. Settes én gang når skjemaet åpnes, ellers ville skjemaet
  // (som søker mens telefonnummeret skrives) tømt sitt eget felt.
  const [skjemaForslag, setSkjemaForslag] = useState(fraForsiden?.forslag || TOMT_FORSLAG);
  const [apner, setApner] = useState(false);

  useEffect(() => {
    setVisSkjema(Boolean(handling?.nyKunde || fraForsiden?.forslag));
    setSok(fraForsiden?.sok || '');
    setSkjemaForslag(fraForsiden?.forslag || TOMT_FORSLAG);
  }, [handling, fraForsiden]);

  const tilbake = (
    <Link to={handling?.tilbake?.til || FORSIDE} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
      <Ikon navn="tilbake" className="w-5 h-5" /> {handling?.tilbake?.tekst || 'Tilbake til kontrollpanelet'}
    </Link>
  );

  if (!handling || !handling.medKunde || (handling.tilgang === 'leder' && !isLeder())) {
    return (
      <div className="mx-auto max-w-7xl space-y-3">
        {tilbake}
        <h1 className="text-xl font-bold text-slate-900">Fant ikke oppgaven</h1>
      </div>
    );
  }

  const utfor = async (kunde) => {
    if (handling.medKunde === 'butikkmodus') {
      setApner(true);
      await apneButikkmodus(kunde, handling.bmodTil || '/');
      return;
    }
    const { til, state } = handling.medKunde(kunde);
    navigate(til, { state });
  };

  const farge = FARGER[handling.farge] || FARGER.info;
  const sokForslag = forslagFraSok(sok);
  const sokForslagTekst = forslagTekst(sokForslag);
  const utenAnsatt = !Cookies.get('selectedEmployee');

  return (
    <>
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      {tilbake}
      <header className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${farge.ikon}`}>
          <Ikon navn={handling.ikon} className="w-6 h-6" />
        </span>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{handling.tittel}</h1>
          <p className="text-slate-600">{handling.nyKunde ? 'Sjekk at kunden ikke finnes fra før.' : 'Steg 1 av 2: Hvilken kunde?'}</p>
        </div>
      </header>

      {utenAnsatt && (
        <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-amber-900">
          Velg hvem du er øverst før du registrerer noe.
        </p>
      )}

      {apner ? (
        <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">Åpner butikkmodus …</p>
      ) : (
        <>
          <section aria-label="Finn kunde" className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <KundeSok id="velg-kunde-sok" sok={sok} setSok={setSok} onVelg={utfor} autoFocus={!handling.nyKunde} />
          </section>

          {visSkjema ? (
            <CustomerForm
              key={JSON.stringify(skjemaForslag)}
              customers={customers}
              addCustomer={addCustomer}
              forslag={skjemaForslag}
              phoneNumber={skjemaForslag.phoneNumber}
              setSearchQuery={setSok}
              onCreated={utfor}
            />
          ) : (
            <button
              type="button"
              onClick={() => { setSkjemaForslag(sokForslag); setVisSkjema(true); }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-sky-300 bg-white px-3 py-3 text-base font-semibold text-sky-800 hover:bg-sky-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            >
              <Ikon navn="personPluss" className="w-7 h-7" />
              <span className="min-w-0 [overflow-wrap:anywhere]">
                {sokForslagTekst ? `Ny kunde: ${sokForslagTekst}` : 'Kunden finnes ikke – registrer ny'}
              </span>
            </button>
          )}
        </>
      )}
    </div>

    {/* Skiplukk: de aktive skiplukkene nederst, i full bredde (vinnsel, 30.10). */}
    {handling.id === 'skiplukk' && (
      <div className="mx-auto max-w-7xl pb-10">
        <AktiveSkiplukk />
      </div>
    )}
    </>
  );
}
